import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, MapPin, Calendar } from 'lucide-react';
import { DateRangePicker } from '../../../shared/components/forms/DateRangePicker';
import { GuestCounter } from '../../../shared/components/forms/GuestCounter';
import {
  formatDateForURL,
  isSameDay,
  formatDateRangeForDisplay,
} from '../../../shared/utils/dateUtils';
import { Range } from 'react-date-range';
import { ActionButton } from '../../../shared/components/ui/ActionButton';

const tanzaniaDestinations = [
  {
    name: 'Serengeti National Park',
    region: 'Northern Tanzania',
    type: 'Wildlife Safari',
  },
  {
    name: 'Ngorongoro Crater',
    region: 'Northern Tanzania',
    type: 'Wildlife Safari',
  },
  {
    name: 'Mount Kilimanjaro',
    region: 'Northern Tanzania',
    type: 'Mountain Trek',
  },
  {
    name: 'Tarangire National Park',
    region: 'Northern Tanzania',
    type: 'Wildlife Safari',
  },
];

interface SearchBoxProps {
  variant?: 'default' | 'glass';
  buttonText?: string;
  initialLocation?: string;
  initialStartDate?: Date;
  initialEndDate?: Date;
  initialGuests?: number;
  title?: React.ReactNode;
  toggle?: React.ReactNode;
  colorTheme?: 'primary' | 'teal';
  onSearch?: (params: {
    location: string;
    startDate: Date;
    endDate?: Date;
    guests: number;
  }) => void;
}

const ButtonLabel: React.FC<{ text?: string }> = ({ text }) => (
  <label className="block text-sm font-semibold text-text-primary mb-2 sm:mb-3">
    {text || '\u00A0'}
  </label>
);

export const SearchBox: React.FC<SearchBoxProps> = ({
  variant = 'default',
  buttonText = 'Search',
  initialLocation = '',
  initialStartDate = new Date(),
  initialEndDate = new Date(),
  initialGuests = 2,
  title,
  toggle,
  colorTheme = 'primary',
  onSearch,
}) => {
  const [, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [guestPickerOpen, setGuestPickerOpen] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [searchLocation, setSearchLocation] = useState(initialLocation);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const locationRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const guestPickerRef = useRef<HTMLDivElement>(null);

  const [dateRange, setDateRange] = useState<Range[]>(() => {
    const startDate =
      initialStartDate instanceof Date && !isNaN(initialStartDate.getTime())
        ? initialStartDate
        : new Date();

    const endDate =
      initialEndDate instanceof Date && !isNaN(initialEndDate.getTime())
        ? initialEndDate
        : new Date(startDate.getTime() + 5 * 24 * 60 * 60 * 1000); // 5 days later

    return [
      {
        startDate,
        endDate,
        key: 'selection',
      },
    ];
  });

  const [guests, setGuests] = useState({
    adults: initialGuests,
    children: 0,
    infants: 0,
  });

  const [dateInput, setDateInput] = useState(() =>
    formatDateRangeForDisplay(
      dateRange[0].startDate!,
      dateRange[0].endDate
    )
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        locationRef.current &&
        !locationRef.current.contains(event.target as Node)
      ) {
        setLocationDropdownOpen(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setDatePickerOpen(false);
      }
      if (
        guestPickerRef.current &&
        !guestPickerRef.current.contains(event.target as Node)
      ) {
        setGuestPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setDateInput(
      formatDateRangeForDisplay(
        dateRange[0].startDate!,
        dateRange[0].endDate
      )
    );
  }, [dateRange]);

  const filteredDestinations = tanzaniaDestinations.filter(
    dest =>
      dest.name.toLowerCase().includes(searchLocation.toLowerCase()) ||
      dest.region.toLowerCase().includes(searchLocation.toLowerCase()) ||
      dest.type.toLowerCase().includes(searchLocation.toLowerCase())
  );

  const handleLocationSelect = (locationName: string) => {
    setSelectedLocation(locationName);
    setSearchLocation(locationName);
    setLocationDropdownOpen(false);
  };

  const handleSearch = () => {
    if (!selectedLocation || !dateRange[0].startDate) {
      return;
    }

    setLoading(true);
    const totalGuests = guests.adults + guests.children;

    const params = new URLSearchParams({
      location: selectedLocation,
      start_date: formatDateForURL(dateRange[0].startDate),
      guests: totalGuests.toString(),
    });

    if (
      dateRange[0].endDate &&
      !isSameDay(dateRange[0].startDate, dateRange[0].endDate)
    ) {
      params.append('end_date', formatDateForURL(dateRange[0].endDate));
    }

    setSearchParams(params);
    onSearch?.({
      location: selectedLocation,
      startDate: dateRange[0].startDate,
      endDate: dateRange[0].endDate || dateRange[0].startDate,
      guests: totalGuests,
    });

    setLoading(false);
  };

  // Helper to parse date range from input string (supports DD-MM-YYYY and DD-MMM-YYYY, and both 'to' and '-' as range separators)
  function parseDateRangeInput(input: string): { startDate: Date | null; endDate: Date | null } {
    const monthMap: Record<string, number> = {
      jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
      jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12
    };
    // Only split on ' to ' or ' - ' (with spaces)
    const parts = input.split(/\s+to\s+|\s+-\s+/i);
    const parse = (str: string) => {
      const trimmed = str.trim();
      let match = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
      if (match) {
        const [, d, m, y] = match;
        const date = new Date(Number(y), Number(m) - 1, Number(d));
        return isNaN(date.getTime()) ? null : date;
      }
      match = trimmed.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
      if (match) {
        const [, d, mmm, y] = match;
        const monthNum = monthMap[mmm.toLowerCase()];
        if (!monthNum) return null;
        const date = new Date(Number(y), monthNum - 1, Number(d));
        return isNaN(date.getTime()) ? null : date;
      }
      return null;
    };
    if (parts.length === 1) {
      const startDate = parse(parts[0]);
      return { startDate, endDate: startDate };
    } else if (parts.length === 2) {
      const startDate = parse(parts[0]);
      const endDate = parse(parts[1]);
      return { startDate, endDate };
    }
    return { startDate: null, endDate: null };
  }

  const containerClass =
    variant === 'glass'
      ? 'bg-white/95 backdrop-blur-lg border border-white/30'
      : 'bg-white/95 backdrop-blur-lg shadow-2xl border border-white/30';

  const colorClasses = {
    primary: {
      icon: 'text-primary-500',
      focus: 'focus:ring-primary-500 focus:border-primary-500',
      hover: 'hover:border-primary-300',
      button: 'bg-primary-500 hover:bg-primary-600',
    },
    teal: {
      icon: 'text-accent-teal-500',
      focus: 'focus:ring-accent-teal-500 focus:border-accent-teal-500',
      hover: 'hover:border-accent-teal-300',
      button: 'bg-accent-teal-500 hover:bg-accent-teal-600',
    },
  };

  const currentColor = colorClasses[colorTheme];

  return (
    <div className="w-full">
      <div
        className={`w-full ${containerClass} rounded-xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 transform transition-all duration-300 hover:shadow-3xl hover:-translate-y-1`}
      >
        {title}
        {/* Subtle gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-50/50 to-accent-coral-50/50 rounded-xl sm:rounded-3xl opacity-60"></div>
        <div className="relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="relative" ref={locationRef}>
              <ButtonLabel text="Where to?" />
              <div className="relative">
                <MapPin className={`absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${currentColor.icon}`} />
                <input
                  type="text"
                  placeholder="Search destinations..."
                  value={searchLocation}
                  onChange={e => {
                    setSearchLocation(e.target.value);
                    setLocationDropdownOpen(true);
                  }}
                  onFocus={() => setLocationDropdownOpen(true)}
                  className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 border-2 border-neutral-200 rounded-xl sm:rounded-xl focus:outline-none focus:ring-2 ${currentColor.focus} bg-white text-text-primary placeholder-text-tertiary transition-all duration-200 ${currentColor.hover} text-sm sm:text-base`}
                />
                {locationDropdownOpen && (
                  <div className="absolute z-[9999] w-full mt-2 bg-white border border-neutral-200 rounded-xl sm:rounded-xl shadow-large max-h-96 overflow-y-auto">
                    <div className="p-2">
                      {filteredDestinations.map((dest, index) => (
                        <button
                          key={index}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl transition-colors flex flex-col ${
                            colorTheme === 'primary' ? 'hover:bg-primary-50' : 'hover:bg-accent-teal-50'
                          }`}
                          onClick={() => handleLocationSelect(dest.name)}
                        >
                          <span className="font-semibold text-text-primary text-sm sm:text-base">
                            {dest.name}
                          </span>
                          <span className="text-xs sm:text-sm text-text-tertiary">
                            {dest.region} • {dest.type}
                          </span>
                        </button>
                      ))}
                      {filteredDestinations.length === 0 && (
                        <div className="px-3 sm:px-4 py-2 sm:py-3 text-text-tertiary text-center text-sm sm:text-base">
                          No destinations found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="relative" ref={datePickerRef}>
              <ButtonLabel text="When?" />
              <div className="relative">
                <Calendar className={`absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${currentColor.icon}`} />
                <input
                  type="text"
                  placeholder="Select dates"
                  value={dateInput}
                  onFocus={() => setDatePickerOpen(true)}
                  onChange={e => {
                    setDateInput(e.target.value);
                  }}
                  onBlur={e => {
                    const { startDate, endDate } = parseDateRangeInput(e.target.value);
                    if (startDate && endDate) {
                      setDateRange([
                        {
                          startDate,
                          endDate,
                          key: 'selection',
                        },
                      ]);
                      setDateInput(
                        formatDateRangeForDisplay(
                          startDate,
                          endDate
                        )
                      );
                    } else {
                      // Reset to last valid value
                      setDateInput(
                        formatDateRangeForDisplay(
                          dateRange[0].startDate!,
                          dateRange[0].endDate
                        )
                      );
                    }
                  }}
                  className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 border-2 border-neutral-200 rounded-xl sm:rounded-xl focus:outline-none focus:ring-2 ${currentColor.focus} bg-white text-text-primary placeholder-text-tertiary transition-all duration-200 ${currentColor.hover} text-sm sm:text-base`}
                />
              </div>
              <DateRangePicker
                dateRange={dateRange}
                onDateChange={ranges => {
                  const selection = ranges.selection;
                  if (selection.startDate) {
                    setDateRange([
                      {
                        startDate: selection.startDate,
                        endDate: selection.endDate || selection.startDate,
                        key: 'selection',
                      },
                    ]);
                  }
                }}
                isOpen={datePickerOpen}
                toggleOpen={() => setDatePickerOpen(!datePickerOpen)}
                pickerRef={datePickerRef}
              />
            </div>
            <GuestCounter
              guests={guests}
              onUpdateGuests={(type, operation) => {
                setGuests(prev => ({
                  ...prev,
                  [type]:
                    operation === 'increment'
                      ? prev[type] + 1
                      : Math.max(0, prev[type] - 1),
                }));
              }}
              isOpen={guestPickerOpen}
              toggleOpen={() => setGuestPickerOpen(!guestPickerOpen)}
              counterRef={guestPickerRef}
              colorTheme={colorTheme}
            />
            <ActionButton
              label="&nbsp;"
              onClick={handleSearch}
              disabled={loading || !selectedLocation}
              colorTheme={colorTheme}
            >
              <Search className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              <span className="hidden sm:inline">
                {loading ? 'Searching...' : buttonText}
              </span>
              <span className="sm:hidden">
                {loading ? 'Searching...' : 'Search'}
              </span>
            </ActionButton>
          </div>
        </div>
        {toggle}
      </div>

      {/* Decorative elements for enhanced floating effect */}
      <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-primary-500/10 to-accent-coral-500/10 rounded-xl sm:rounded-3xl blur-xl opacity-60 -z-10"></div>
      <div className="absolute -inset-1 sm:-inset-2 bg-gradient-to-r from-primary-500/5 to-accent-coral-500/5 rounded-xl sm:rounded-3xl blur-lg opacity-80 -z-10"></div>
    </div>
  );
};
